import React, { useEffect, useState } from 'react';
import ConfirmationModal from '../ui/ConfirmationModal';
import { DatePicker } from '../ui/DatePicker';
import { dealerToday } from '../../utils/dealerDate';

/**
 * MarkPaidConfirm — IDI-518. Every Mark paid (invoice page, row ⋮, bulk)
 * confirms with a "Paid on" date: default today (dealer-local), no future
 * dates. One date per bulk batch. `onConfirm(paidDate)` receives the chosen
 * YYYY-MM-DD; the server re-checks it (mark_paid paid_date).
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {number} [props.count]  bulk size; >1 titles "Mark N invoices paid?"
 * @param {string} [props.invoiceNumber]
 * @param {boolean} [props.busy]
 * @param {(paidDate: string) => void} props.onConfirm
 * @param {() => void} props.onClose
 */
export default function MarkPaidConfirm({ open, count, invoiceNumber, busy = false, onConfirm, onClose }) {
  const [paidOn, setPaidOn] = useState(() => dealerToday());
  const [error, setError] = useState('');

  // Each opening starts from today.
  useEffect(() => {
    if (open) {
      setPaidOn(dealerToday());
      setError('');
    }
  }, [open]);

  const today = dealerToday();
  const bulk = typeof count === 'number' && count !== 1;
  const title = bulk
    ? `Mark ${count} invoices paid?`
    : `Mark ${invoiceNumber ? `${invoiceNumber} ` : 'invoice '}paid?`;

  const handleConfirm = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(paidOn || '')) {
      setError('Choose the date it was paid.');
      return;
    }
    if (paidOn > today) {
      setError('The paid date cannot be in the future.');
      return;
    }
    onConfirm?.(paidOn);
  };

  return (
    <ConfirmationModal
      open={open}
      title={title}
      confirmLabel="Mark paid"
      busy={busy}
      onConfirm={handleConfirm}
      onClose={onClose}
    >
      <div className="mt-3" data-testid="mark-paid-confirm">
        <DatePicker
          label="Paid on"
          aria-label="Paid on"
          value={paidOn}
          max={today}
          onChange={(v) => { setPaidOn(v); setError(''); }}
          error={error || undefined}
        />
      </div>
    </ConfirmationModal>
  );
}
